import * as RT from '@radix-ui/react-tooltip'
import type { ReactNode } from 'react'
import { cn } from './cn'
import { usePrivateAttrs } from './privateArea'

export const TooltipProvider = RT.Provider

export interface TooltipProps {
  content: ReactNode
  children: ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  delayDuration?: number
  /** Render the children without a tooltip (for example when a label is already visible). */
  disabled?: boolean
  className?: string
}

/** Small dark label on hover and focus (inverse surface, 12/500, radius 8). */
export function Tooltip({ content, children, side = 'top', align = 'center', delayDuration = 300, disabled, className }: TooltipProps) {
  const priv = usePrivateAttrs() // a tooltip in a private area (a message's time, an address) is private too
  if (disabled || content == null || content === '') return <>{children}</>
  return (
    <RT.Provider delayDuration={delayDuration} skipDelayDuration={200}>
      <RT.Root>
        <RT.Trigger asChild>{children}</RT.Trigger>
        <RT.Portal>
          <RT.Content
            {...priv}
            side={side}
            align={align}
            sideOffset={6}
            collisionPadding={8}
            className={cn(
              'z-[60] max-w-[260px] rounded-lg bg-inverse px-2 py-1 text-[12px] leading-4 font-medium text-on-inverse shadow-pop',
              'data-[state=delayed-open]:animate-pop-in data-[state=instant-open]:animate-fade-in data-[state=closed]:animate-pop-out',
              className,
            )}
          >
            {content}
          </RT.Content>
        </RT.Portal>
      </RT.Root>
    </RT.Provider>
  )
}
